import { useState, useRef, type ChangeEvent } from 'react';

interface WhatsAppRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Rich text editor with WhatsApp-compatible formatting
 * Supports: Bold (*), Italic (_), Strikethrough (~), Monospace (`)
 */
export const WhatsAppRichTextEditor = ({
  value,
  onChange,
  placeholder = 'Type your response...',
  disabled = false,
}: WhatsAppRichTextEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  /**
   * Apply formatting by wrapping selected text with WhatsApp markdown syntax
   */
  const applyFormatting = (prefix: string, suffix: string = prefix) => {
    if (!textareaRef.current || disabled) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    if (selectedText) {
      // Wrap selected text
      const newValue =
        value.substring(0, start) +
        prefix +
        selectedText +
        suffix +
        value.substring(end);
      onChange(newValue);

      // Restore selection after formatting
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + prefix.length,
          end + prefix.length
        );
      }, 0);
    } else {
      // Insert formatting markers at cursor position
      const newValue =
        value.substring(0, start) +
        prefix +
        suffix +
        value.substring(end);
      onChange(newValue);

      // Place cursor between markers
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + prefix.length,
          start + prefix.length
        );
      }, 0);
    }
  };

  /**
   * Convert WhatsApp markdown to HTML for preview
   */
  const convertToPreview = (text: string): string => {
    let html = text;

    // Bold: *text*
    html = html.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');

    // Italic: _text_
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

    // Strikethrough: ~text~
    html = html.replace(/~([^~]+)~/g, '<del>$1</del>');

    // Monospace: `text`
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Line breaks
    html = html.replace(/\n/g, '<br />');

    return html;
  };

  const characterCount = value.length;
  const isEmpty = value.trim().length === 0;

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex items-center gap-1 border-b border-gray-200 pb-2">
        <button
          type="button"
          onClick={() => applyFormatting('*')}
          disabled={disabled}
          className="px-3 py-1.5 text-sm font-bold border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Bold (*text*)"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => applyFormatting('_')}
          disabled={disabled}
          className="px-3 py-1.5 text-sm italic border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Italic (_text_)"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => applyFormatting('~')}
          disabled={disabled}
          className="px-3 py-1.5 text-sm line-through border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Strikethrough (~text~)"
        >
          S
        </button>
        <button
          type="button"
          onClick={() => applyFormatting('`')}
          disabled={disabled}
          className="px-3 py-1.5 text-sm font-mono border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Monospace (\`text\`)"
        >
          {'</>'}
        </button>

        <div className="flex-1" />

        {/* Preview Toggle */}
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          disabled={disabled}
          className={`px-3 py-1.5 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed ${
            showPreview
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'border-gray-300 hover:bg-gray-50'
          }`}
        >
          {showPreview ? 'Edit' : 'Preview'}
        </button>
      </div>

      {/* Editor / Preview */}
      {showPreview ? (
        <div className="min-h-[200px] p-4 border border-gray-300 rounded-lg bg-gray-50">
          {isEmpty ? (
            <p className="text-gray-400 italic">Nothing to preview</p>
          ) : (
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: convertToPreview(value) }}
            />
          )}
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full min-h-[200px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-y"
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
        />
      )}

      {/* Character Count and Help Text */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="space-y-1">
          <p>
            <strong>Formatting:</strong> *bold* _italic_ ~strikethrough~ \`monospace\`
          </p>
          {isEmpty && (
            <p className="text-red-500">Response cannot be empty</p>
          )}
        </div>
        <span className={characterCount > 1000 ? 'text-orange-600 font-semibold' : ''}>
          {characterCount} characters
        </span>
      </div>
    </div>
  );
};
